import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { shopApi, SHOP_TOKEN_KEY, formatApiError } from "@/lib/api";

const CustomerContext = createContext(null);

export const CustomerProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [wishlist, setWishlist] = useState([]); // array of product ids
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(SHOP_TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await shopApi.get("/customer/me");
      setUser({ id: data.id, name: data.name, email: data.email, phone: data.phone });
      setWishlist(data.wishlist || []);
    } catch {
      localStorage.removeItem(SHOP_TOKEN_KEY);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const auth = async (path, payload) => {
    try {
      const { data } = await shopApi.post(`/customer/${path}`, payload);
      localStorage.setItem(SHOP_TOKEN_KEY, data.token);
      setUser(data.user);
      await loadMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const login = (email, password) => auth("login", { email, password });
  const register = (payload) => auth("register", payload);
  const logout = () => { localStorage.removeItem(SHOP_TOKEN_KEY); setUser(null); setWishlist([]); };

  const toggleWishlist = async (productId) => {
    if (!user) return { needAuth: true };
    const { data } = await shopApi.post(`/customer/wishlist/${productId}`);
    setWishlist(data.wishlist);
    return { active: data.active };
  };

  const inWishlist = (id) => wishlist.includes(id);

  return (
    <CustomerContext.Provider value={{ user, loading, wishlist, login, register, logout, toggleWishlist, inWishlist, refresh: loadMe }}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => useContext(CustomerContext);
